import React from 'react';
import { observer } from 'mobx-react';
import { autorun, observable } from 'mobx';
import { AppUI } from '@seedlang/state';
import ErrorBoundary from 'components/hoc/error_boundary';
import MantineConfirmModal from 'components/mantine_confirm_modal';
import styled from '@emotion/styled';
import Text from 'components/text';
import TextDisplay from 'components/text_display';
import PageTitle from 'components/common/page_title';
import Stack, { gaps } from 'components/layout/stack';
import { Theme } from '@seedlang/constants';
import { niceJoin } from '@seedlang/utils';
import { xor } from 'lodash';
import { Switch } from '@mantine/core';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${gaps.md};
  text-align: start;
`;

const EmailPreferenceRowWrapper = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 3fr) minmax(0, 1fr);
  gap: ${gaps.sm} 50px;
  align-items: center;

  @media (max-width: 600px) {
    grid-template-columns: 1fr;
  }
`;

const EmailPreferenceHeading = styled.div`
  font-size: 15px;
  font-weight: bold;
`;

const EmailPreferenceDescription = styled.div`
  color: ${Theme.darkGray};
  font-size: 13px;
  margin-top: 4px;
`;

const EmailPreferenceRow = ({ children, description, heading }) => (
  <EmailPreferenceRowWrapper>
    <div>
      <EmailPreferenceHeading>{heading}</EmailPreferenceHeading>
      {description && <EmailPreferenceDescription>{description}</EmailPreferenceDescription>}
    </div>
    {children}
  </EmailPreferenceRowWrapper>
);

export const SettingsEmail = ({
  accentColor = '#2e8629',
  enableMailerlite,
  groupSubscribeConfirmOpened = false,
  isLoading = false,
  mailerliteGroups = [],
  onboarding,
  onCloseGroupSubscribeConfirm,
  onClosePlatformUnsubscribeConfirm,
  onConfirmGroupSubscribe,
  onConfirmPlatformUnsubscribe,
  onToggleGroupSelection,
  onTogglePlatformEmails,
  platformUnsubscribeConfirmOpened = false,
  platformUnsubscribeGroupNames = [],
  platformEmailsSubscribed,
  selectedGroupIds = [],
  teamName,
}) => {
  const learningLanguage = teamName?.replace(/^Easy /, '');

  return (
    <Wrapper>
      {
        !onboarding && (
          <>
            <PageTitle>Email Preferences</PageTitle>
            <Text heading='2'>Email Preferences</Text>
          </>
        )
      }
      <Stack alignItems='stretch' gap='sm'>
        <EmailPreferenceRow
          heading='Account-related emails'
          description="We'll always send essential emails about your account and membership such as password resets, billing, and important service notices."
        >
          <Switch
            aria-label='Account-related emails: Always on'
            checked
            color={accentColor}
            disabled
            label='Always on'
          />
        </EmailPreferenceRow>
        <EmailPreferenceRow
          heading='Platform tips & emails'
          description={platformEmailsSubscribed ? (
            `${teamName} sends you tips for using the platform and learning ${learningLanguage}, plus its own offers.`
          ) : (
            <>
              By subscribing again, you agree to receive these emails.
              <br />
              You can unsubscribe anytime.
            </>
          )}
        >
          <Switch
            aria-label={`Platform tips & emails: ${platformEmailsSubscribed ? 'Subscribed' : 'Unsubscribed'}`}
            checked={Boolean(platformEmailsSubscribed)}
            color={accentColor}
            disabled={isLoading}
            label={platformEmailsSubscribed ? 'Subscribed' : 'Unsubscribed'}
            onChange={onTogglePlatformEmails}
          />
        </EmailPreferenceRow>
        {
          enableMailerlite && mailerliteGroups.filter(group => group.exposeAsPreference).map(group => {
            const subscribed = selectedGroupIds.includes(group.id);

            return (
              <EmailPreferenceRow
                key={group.id}
                heading={group.name}
                description={group.helperText && (
                  <TextDisplay
                    linkColor={accentColor}
                    markdown={group.helperText}
                    removeTopMargin
                  />
                )}
              >
                <Switch
                  aria-label={`${group.name}: ${subscribed ? 'Subscribed' : 'Unsubscribed'}`}
                  checked={subscribed}
                  color={accentColor}
                  disabled={isLoading}
                  label={subscribed ? 'Subscribed' : 'Unsubscribed'}
                  onChange={() => onToggleGroupSelection(group.id)}
                />
              </EmailPreferenceRow>
            );
          })
        }
      </Stack>
      <MantineConfirmModal
        bodyText={`This will also unsubscribe you from ${niceJoin(platformUnsubscribeGroupNames)}.`}
        cancelLabel='Cancel'
        confirmLabel='Unsubscribe'
        onClose={onClosePlatformUnsubscribeConfirm}
        onConfirm={onConfirmPlatformUnsubscribe}
        opened={platformUnsubscribeConfirmOpened}
        title='Unsubscribe'
      />
      <MantineConfirmModal
        bodyText='This will also subscribe you to platform tips & emails.'
        cancelLabel='Cancel'
        confirmColor={accentColor}
        confirmLabel='Subscribe'
        onClose={onCloseGroupSubscribeConfirm}
        onConfirm={onConfirmGroupSubscribe}
        opened={groupSubscribeConfirmOpened}
        title='Subscribe'
      />
    </Wrapper>
  );
};

@observer
class SettingsEmailWrapper extends React.Component {
  @observable userAgreedToEmail;
  @observable groupSubscribeConfirmOpened = false;
  @observable mailerliteGroups = [];
  @observable pendingGroupId;
  @observable platformUnsubscribeConfirmOpened = false;
  @observable platformUnsubscribeGroupNames = [];
  @observable selectedGroupIds = [];

  constructor(props) {
    super(props);
    this.disposer = autorun(() => {
      if (AppUI.userIsLoaded && !this.userIsLoaded) {
        this.userIsLoaded = true;
        this.userAgreedToEmail = AppUI.user?.currentUserGroup?.userAgreedToEmail === 'true';
      }
    });
  }

  componentDidMount() {
    if (!AppUI.site.enableMailerlite) return;

    AppUI.userGroupStore.getMailerliteGroups({}, response => {
      if (Array.isArray(response)) {
        this.mailerliteGroups = response;
        this.selectedGroupIds = response.filter(g => g.selected).map(g => g.id);
      } else {
        this.mailerliteGroups = [];
        this.selectedGroupIds = [];
      }
    });
  }

  componentWillUnmount() {
    this.disposer && this.disposer();
  }
  showPreferenceUpdateError = () => {
    AppUI.serverStore.setUserFriendlyErrorMessage(
      'We could not update your email preferences. Please try again later.',
    );
    AppUI.loadUser();
  }
  updateEmailConfirmation = (value, callback, failureCallback) => {
    let requestFinished = false;
    const handleFailure = () => {
      if (requestFinished) return;
      requestFinished = true;
      failureCallback && failureCallback();
    };

    AppUI.userGroupStore.update({
      ids: {userGroupId: AppUI.user.currentUserGroup?.id},
      data: {user_agreed_to_email: value ? 'true' : 'false'},
      failureCallback: handleFailure,
      onRequestError: handleFailure,
    }, response => {
      if (requestFinished) return;
      if (!response?.success) {
        handleFailure();
        return;
      }
      requestFinished = true;
      this.userAgreedToEmail = value;
      AppUI.loadUser();
      callback && callback();
    });
  }
  saveGroupSelection = (groupIds, callback, failureCallback) => {
    let requestFinished = false;
    const handleFailure = () => {
      if (requestFinished) return;
      requestFinished = true;
      failureCallback && failureCallback();
    };

    AppUI.userGroupStore.updateMailerliteGroups({
      data: { group_ids: groupIds },
      failureCallback: handleFailure,
      onRequestError: handleFailure,
    }, response => {
      if (requestFinished) return;
      if (!response?.success) {
        handleFailure();
        return;
      }
      requestFinished = true;
      this.selectedGroupIds = groupIds;
      callback && callback();
    });
  }
  requestPlatformEmailToggle = () => {
    if (this.userAgreedToEmail && this.selectedGroupIds.length > 0) {
      this.platformUnsubscribeGroupNames = this.mailerliteGroups
        .filter(group => this.selectedGroupIds.includes(group.id))
        .map(group => group.name);
      this.platformUnsubscribeConfirmOpened = true;
    } else {
      this.updateEmailConfirmation(
        !this.userAgreedToEmail,
        null,
        this.showPreferenceUpdateError,
      );
    }
  }
  closePlatformUnsubscribeConfirm = () => {
    this.platformUnsubscribeConfirmOpened = false;
  }
  confirmPlatformUnsubscribe = () => {
    this.updateEmailConfirmation(
      false,
      () => {
        this.saveGroupSelection(
          [],
          this.closePlatformUnsubscribeConfirm,
          () => {
            this.selectedGroupIds = [];
            this.closePlatformUnsubscribeConfirm();
            this.showPreferenceUpdateError();
          },
        );
      },
      () => {
        this.closePlatformUnsubscribeConfirm();
        this.showPreferenceUpdateError();
      },
    );
  }
  updateGroupSelection = (groupId) => {
    this.saveGroupSelection(
      xor(this.selectedGroupIds, [groupId]),
      null,
      this.showPreferenceUpdateError,
    );
  }
  requestGroupSelectionToggle = (groupId) => {
    const subscribing = !this.selectedGroupIds.includes(groupId);
    if (!this.userAgreedToEmail && subscribing) {
      this.pendingGroupId = groupId;
      this.groupSubscribeConfirmOpened = true;
    } else {
      this.updateGroupSelection(groupId);
    }
  }
  closeGroupSubscribeConfirm = () => {
    this.groupSubscribeConfirmOpened = false;
    this.pendingGroupId = null;
  }
  confirmGroupSubscribe = () => {
    const groupId = this.pendingGroupId;
    const selectedGroupIds = xor(this.selectedGroupIds, [groupId]);
    const finishWithError = () => {
      this.closeGroupSubscribeConfirm();
      this.showPreferenceUpdateError();
    };

    this.updateEmailConfirmation(
      true,
      () => {
        this.saveGroupSelection(
          selectedGroupIds,
          this.closeGroupSubscribeConfirm,
          () => this.updateEmailConfirmation(false, finishWithError, finishWithError),
        );
      },
      finishWithError,
    );
  }

  render() {
    return (
      <SettingsEmail
        accentColor={AppUI.site.accentColor}
        enableMailerlite={AppUI.site.enableMailerlite}
        groupSubscribeConfirmOpened={this.groupSubscribeConfirmOpened}
        isLoading={
          AppUI.userGroupStore.hasOutstandingRequests ||
          AppUI.authUserStore.hasOutstandingRequests ||
          AppUI.userRequestPending
        }
        mailerliteGroups={this.mailerliteGroups}
        onboarding={this.props.onboarding}
        onCloseGroupSubscribeConfirm={this.closeGroupSubscribeConfirm}
        onClosePlatformUnsubscribeConfirm={this.closePlatformUnsubscribeConfirm}
        onConfirmGroupSubscribe={this.confirmGroupSubscribe}
        onConfirmPlatformUnsubscribe={this.confirmPlatformUnsubscribe}
        onToggleGroupSelection={this.requestGroupSelectionToggle}
        onTogglePlatformEmails={this.requestPlatformEmailToggle}
        platformEmailsSubscribed={this.userAgreedToEmail}
        platformUnsubscribeConfirmOpened={this.platformUnsubscribeConfirmOpened}
        platformUnsubscribeGroupNames={this.platformUnsubscribeGroupNames}
        selectedGroupIds={this.selectedGroupIds}
        teamName={AppUI.site.teamName}
      />
    );
  }
}

export default ErrorBoundary(SettingsEmailWrapper);
